import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, CheckCircle, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('upcoming');

  useEffect(() => {
    loadEvents();
  }, [tab]);

  const loadEvents = async () => {
    try {
      const response = await axios.get(`${API}/events?status=${tab}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Error loading events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="events-page">
      <div>
        <h1 className="text-4xl font-bold mb-2" style={{ color: '#0F172A' }}>Events</h1>
        <p className="text-muted-foreground">Stay updated with our activities</p>
      </div>
      
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="upcoming-tab">Upcoming</TabsTrigger>
          <TabsTrigger value="past" data-testid="past-tab">Past</TabsTrigger>
        </TabsList>
        
        <TabsContent value={tab} className="mt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : events.length === 0 ? (
            <Card data-testid="no-events">
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">No {tab} events</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
              {events.map((event) => (
                <Link key={event.event_id} to={`/app/events/${event.event_id}`}>
                  <Card className="hover-lift cursor-pointer h-full" data-testid={`event-card-${event.event_id}`}>
                    {event.banner_image_url && (
                      <div className="h-48 overflow-hidden rounded-t-xl">
                        <img
                          src={getImageUrl(event.banner_image_url)}
                          alt={event.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold mb-3" style={{ color: '#0F172A' }}>
                        {event.title}
                      </h3>
                      {event.description && (
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {event.description}
                        </p>
                      )}
                      
                      {/* RSVP Badge */}
                      {event.confirmation_required && (
                        <div className="mb-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            <Users className="w-3 h-3" />
                            RSVP Required
                          </span>
                        </div>
                      )}
                      
                      <div className="space-y-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(event.date_time_start)}</span>
                        </div>
                        {event.venue && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{event.venue}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EventsPage;
